import { useState } from 'react';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { ChevronDown } from '@mint-lib/icons';
import {
  Chip,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@mint-lib/tw-components';
import {
  CalendarRange,
  type DateRange,
} from '@mint-lib/tw-components/patterns';

export type ChipDateRangePickerProps = {
  selected: DateRange;
  placeholder: string;
  onApply: (dateRange: DateRange) => void;
  onReset?: (dateRange?: DateRange) => void;
  isDisabled?: boolean;
};

const ChipDateRangePicker = ({
  selected,
  onApply,
  onReset,
  placeholder,
  isDisabled = false,
}: ChipDateRangePickerProps) => {
  const [open, setOpen] = useState(false);

  const [month, setMonth] = useState<Date | undefined>(new Date());
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    selected?.from ? selected : undefined,
  );

  const { formattedDateRange, formattedDateFrom } = useDateFormatter(
    selected?.from,
    selected?.to,
    'dd MMM',
  );

  const label = formattedDateRange || formattedDateFrom || placeholder;

  const handleSelect = (dateRange?: DateRange) => {
    setSelectedRange(dateRange);
  };

  const handleApply = () => {
    if (selectedRange) {
      onApply(selectedRange);
    }

    setOpen(false);
  };

  const handleReset = () => {
    setSelectedRange(undefined);
    onReset?.();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedRange(selected?.from ? selected : undefined);
    }
    setOpen(isOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Chip
          color={selected?.from ? 'tertiary' : 'secondary'}
          variant={selected?.from ? 'filled' : 'outline'}
          size="large"
          disabled={isDisabled}
        >
          {label}
          <ChevronDown />
        </Chip>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="z-popover w-auto p-0"
        >
          <CalendarRange
            selected={{ from: selectedRange?.from, to: selectedRange?.to }}
            onSelect={handleSelect}
            month={month}
            onMonthChange={setMonth}
            disabledDates={{ before: new Date() }}
            onApply={handleApply}
            onReset={handleReset}
          />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { ChipDateRangePicker };
